/**
 * Advanced Options Types
 *
 * This module defines types and constants for the QR code advanced options feature.
 * Advanced options allow users to add optional query parameters to generated QR URLs.
 *
 * Requirements: 2.2, 3.2, 3.3, 4.2, 5.2
 */

/**
 * Supported language codes for language override option
 * Requirements 3.2, 3.3: Language dropdown includes en, de, es, fr, ja, ko, sv
 */
export type LanguageCode = 'en' | 'de' | 'es' | 'fr' | 'ja' | 'ko' | 'sv';

/**
 * Language options for the dropdown selector
 * Requirement 3.2: Language dropdown includes none and all supported language codes
 */
export const LANGUAGE_OPTIONS: readonly { readonly value: LanguageCode | null; readonly label: string }[] = [
  { value: null, label: 'None' },
  { value: 'en', label: 'English (en)' },
  { value: 'de', label: 'German (de)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'ja', label: 'Japanese (ja)' },
  { value: 'ko', label: 'Korean (ko)' },
  { value: 'sv', label: 'Swedish (sv)' },
] as const;

/**
 * Advanced options configuration for QR code URL generation
 *
 * Requirements:
 * - 2.2: Experimental features toggle defaults to disabled
 * - 3.2, 3.3: Language override dropdown with supported codes, defaults to none
 * - 4.2: Swing capture toggle defaults to disabled
 * - 5.2: Game mode input defaults to empty
 */
export interface AdvancedOptions {
  /** Whether advanced options are enabled (controls visibility and URL generation) */
  readonly enabled: boolean;

  /** Enable experimental features - adds exp=true to URL when enabled */
  readonly experimentalFeatures: boolean;

  /** Force a specific language - adds lang=<code> to URL when set */
  readonly languageOverride: LanguageCode | null;

  /** Enable swing capture functionality - adds swing-capture=true to URL when enabled */
  readonly swingCapture: boolean;

  /** Open a specific game mode - adds mode=<value> to URL when non-empty */
  readonly gameMode: string;
}

/**
 * Default values for advanced options
 *
 * Requirements:
 * - 2.2: Experimental features defaults to disabled
 * - 3.3: Language override defaults to none
 * - 4.2: Swing capture defaults to disabled
 * - 5.2: Game mode defaults to empty
 */
export const DEFAULT_ADVANCED_OPTIONS: AdvancedOptions = {
  enabled: false,
  experimentalFeatures: false,
  languageOverride: null,
  swingCapture: false,
  gameMode: '',
} as const;
